import React, { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import { Button, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { constants } from "../../../constants/common";
import { getData } from "../../../actions/apiCall";
import { useSelector } from "react-redux";
import { fetchWithCache } from "../../../actions/auth";

const { Title, Text } = Typography;

interface MonthData {
    title: string; // "Jul 2024"
    dateRange: string;
    status: "completed" | "current" | "upcoming" | "pending";
    rawStatus?: string; // "finalized", "in_progress", etc.
}

const statusColor = {
    completed: "bg-green-500",
    current: "bg-blue-500",
    upcoming: "bg-yellow-500",
    pending: "bg-yellow-500"
};

const RunPayRollMonths: React.FC = () => {
    const visibleCount = 8;
    const preload = 15;

    const [startOffset, setStartOffset] = useState(-preload);
    const [endOffset, setEndOffset] = useState(preload);
    const [months, setMonths] = useState<MonthData[]>([]);
    const [activeCard, setActiveCard] = useState<MonthData | null>(null);
    const [startIndex, setStartIndex] = useState(preload);
    const [startMonth, setStartMonth] = useState<Moment | null>(null);

    const dispatch = useDispatch();

    useEffect(() => {
        getPayrollSetting();
    }, []);

    const getPayrollSetting = async () => {
        try {
            const resData = await dispatch(getData('/payroll-setting'));
            const startMonthStr = resData?.data?.startPayRollMonth;

            if (startMonthStr) {
                const parsedStart = moment(startMonthStr, "MM-YYYY");
                setStartMonth(parsedStart);

                await getPayrollData(parsedStart); // ✅ wait for parsedStart
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getPayrollData = async (parsedStart: Moment) => {
        try {
            const from = parsedStart.clone().add(startOffset, "months").format("MM-YYYY");
            const to = parsedStart.clone().add(endOffset, "months").format("MM-YYYY");

            const payrollRes = await dispatch(
                getData(`/run-payroll/get-payroll-status?from=${from}&to=${to}`)
            );
            const statusMap: Record<string, string> = {};
            payrollRes?.data?.forEach((entry: any) => {
                const title = moment(entry.month, "MM-YYYY").format("MMM YYYY");
                statusMap[title] = entry.status; // e.g. "finalized", "in_progress"
            });

            const bufferMonths: MonthData[] = [];
            for (let i = startOffset; i <= endOffset; i++) {
                const date = parsedStart.clone().add(i, "months");
                const title = date.format("MMM YYYY");
                const rawStatus = statusMap[title];

                let status: MonthData["status"] = "pending";
                if (rawStatus === "finalized") status = "completed";
                else if (rawStatus === "in_progress") status = "current";
                else if (date.isSame(moment(), "month")) status = "current";
                else if (date.isAfter(moment(), "month")) status = "upcoming";

                bufferMonths.push({
                    title,
                    dateRange: `${date.clone().startOf("month").format("MMM DD")} - ${date.clone().endOf("month").format("MMM DD")}`,
                    status,
                    rawStatus: rawStatus?.replace(/_/g, ' ').toUpperCase(),
                });
            }

            setMonths(bufferMonths);
        } catch (err) {
            console.error("Error fetching payroll data:", err);
        }
    };

    const onClick = (month: MonthData) => {
        const current_month = moment(month.title).format('MM-YYYY');

        dispatch(
            fetchWithCache({
                key: `payroll-${current_month}`,
                url: '/payroll-run',
                query: { month: current_month },
                cacheDuration: 5 * 60 * 1000,
                reloadData: true
            })
        )

        dispatch({
            type: constants.PARAMS,
            params: {
                selectedMonth: month.title,
                selectedDuration: month.dateRange,
            },
        });
        setActiveCard(month);
    };

    const extendBufferLeft = () => {
        if (!startMonth) return;

        const potentialStart = startMonth.clone().add(startOffset - 30, "months");
        if (potentialStart.isBefore(startMonth, "month")) return;

        const newStart = startOffset - 30;
        const newMonths: MonthData[] = [];
        for (let i = newStart; i < startOffset; i++) {
            newMonths.push(generateMonth(startMonth, i));
        }
        setMonths((prev) => [...newMonths, ...prev]);
        setStartOffset(newStart);
        setStartIndex((prev) => prev + 30);
    };

    const extendBufferRight = () => {
        if (!startMonth) return;

        const newEnd = endOffset + 30;
        const newMonths: MonthData[] = [];
        for (let i = endOffset + 1; i <= newEnd; i++) {
            newMonths.push(generateMonth(startMonth, i));
        }
        setMonths((prev) => [...prev, ...newMonths]);
        setEndOffset(newEnd);
    };

    const generateMonth = (baseDate: Moment, offset: number): MonthData => {
        const date = baseDate.clone().add(offset, "months");
        const title = date.format("MMM YYYY");
        const startDate = date.clone().startOf("month").format("MMM DD");
        const endDate = date.clone().endOf("month").format("MMM DD");

        return {
            title,
            dateRange: `${startDate} - ${endDate}`,
            status: "upcoming", // placeholder, will be overwritten if needed
        };
    };

    const shiftLeft = () => {
        if (!startMonth || startIndex === 0) return;

        const currentLeftMonth = generateMonth(startMonth, startOffset + startIndex - 1);
        const currentLeftMoment = moment(currentLeftMonth.title, "MMM YYYY");

        if (currentLeftMoment.isSameOrBefore(startMonth, "month")) return;

        if (startIndex < 5) extendBufferLeft();
        setStartIndex((prev) => Math.max(0, prev - 5));
    };

    const shiftRight = () => {
        if (!startMonth) return;

        if (startIndex + visibleCount >= months.length - 5) extendBufferRight();
        if (startIndex + visibleCount < months.length) {
            setStartIndex((prev) => prev + 5);
        }
    };

    return (
        <div className="p-4 bg-white rounded shadow">
            <div className="flex items-center space-x-3">
                <Button
                    onClick={shiftLeft}
                    type="default"
                    disabled={
                        !startMonth ||
                        moment(months[startIndex]?.title, "MMM YYYY").isSameOrBefore(startMonth, "month")
                    }
                    className="p-2 rounded-full"
                >
                    <LeftOutlined />
                </Button>

                <div className="overflow-hidden w-full">
                    <div
                        className="flex transition-transform duration-300 ease-in-out gap-3"
                        style={{
                            transform: `translateX(-${startIndex * 172}px)`,
                            width: `${months.length * 172}px`,
                        }}
                    >
                        {months.map((month, idx) => (
                            <div
                                key={idx}
                                onClick={() => month.status !== 'upcoming' ? onClick(month) : ''}
                                className={`w-[160px] min-w-[160px] max-w-[160px] border rounded p-3 cursor-pointer shadow-sm hover:shadow-md text-center flex flex-col items-center justify-center ${activeCard?.title === month.title
                                    ? "border-blue-500 shadow-lg"
                                    : "border-gray-200 bg-white"}`}
                            >
                                <Title level={5} className="!mb-1">
                                    {month.title}
                                </Title>
                                <Text type="secondary">{month.dateRange}</Text>
                                <div
                                    className={`mt-2 inline-block text-white text-xs px-2 py-1 rounded ${statusColor[month.status]}`}
                                >
                                    {month.status.toUpperCase()}
                                </div>
                                {month.rawStatus && (
                                    <div className="text-[10px] text-gray-500 mt-1 uppercase">
                                        {month.rawStatus}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <Button onClick={shiftRight} className="p-2 rounded-full">
                    <RightOutlined />
                </Button>
            </div>
        </div>
    );
};

export default RunPayRollMonths;

